import { useMemo, useState, useCallback } from "react";

import { atom, useAtom } from "jotai";

import { RebudgetingTableStore } from "./use-table-store";

export type CellIdentifier = {
  columnName: string;
  rowId: string;
};

const DELINEATOR = "---";

export const CellSelectionAtom = atom<Map<string, any>>(new Map());
export const constructCellId = (cell: CellIdentifier) => {
  return `${cell.columnName}${DELINEATOR}${cell.rowId}`;
};

export const useCellSelection = <T extends CellIdentifier>() => {
  const [cellsMap, setCellsMap] = useAtom(CellSelectionAtom, {
    store: RebudgetingTableStore,
  });

  const getCell = useCallback(
    (cell: CellIdentifier) => {
      return cellsMap.get(constructCellId(cell));
    },
    [cellsMap],
  );

  const cells: T[] = useMemo(() => {
    return Array.from(cellsMap.values());
  }, [cellsMap]);

  const addCell = useCallback(
    (cell: T) => {
      const cellId = constructCellId(cell);
      setCellsMap((prev) => new Map(prev).set(cellId, cell));
    },
    [setCellsMap],
  );

  const removeCell = useCallback(
    (cell: CellIdentifier) => {
      setCellsMap((prev) => {
        const newMap = new Map(prev);
        newMap.delete(constructCellId(cell));
        return newMap;
      });
    },
    [setCellsMap],
  );

  const toggleCell = useCallback(
    (cell: T) => {
      const cellId = constructCellId(cell);
      setCellsMap((prev) => {
        const newMap = new Map(prev);
        if (newMap.has(cellId)) {
          newMap.delete(cellId);
        } else {
          newMap.set(cellId, cell);
        }
        return newMap;
      });
    },
    [setCellsMap],
  );

  const setCells = useCallback(
    (newCells: T[]) => {
      setCellsMap(
        new Map(newCells.map((cell) => [constructCellId(cell), cell])),
      );
    },
    [setCellsMap],
  );

  const clearCells = useCallback(() => {
    setCellsMap(new Map());
  }, [setCellsMap]);

  return [
    cells,
    setCells,
    {
      getCell,
      addCell,
      removeCell,
      toggleCell,
      clearCells,
    },
  ] as const;
};
